import React, { useState } from 'react';
import styled from 'astroturf/react';
import { PaperUpdateUnlinked, Paper } from 'js/social/shared/types';
import { NoThumbnailIcon } from 'js/social/shared/Icons';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/social/shared/design_system/Button';
import DownloadIcon from 'js/design_system/icons/arrow_downward.svg';
import PlusIcon from 'js/design_system/icons/add.svg';
import CheckIcon from 'js/design_system/icons/check.svg';
import ImpressionWatcher, {
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';
import { Reader } from 'js/social/Reader/Reader';
import useScrollLock from 'js/utils/hooks/useScrollLock';
import { SimplePaperPodcast } from 'js/podcast_ai/SimplePaperPodcast';
import _Container from "PaperContainer-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/PaperContainer.tsx?styleId=Container";
import _Metadata from "PaperContainer-Metadata.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/PaperContainer.tsx?styleId=Metadata";
import _PaperTitle from "PaperContainer-PaperTitle.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/PaperContainer.tsx?styleId=PaperTitle";
import _MetadataText from "PaperContainer-MetadataText.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/PaperContainer.tsx?styleId=MetadataText";
import _PaperThumbnail from "PaperContainer-PaperThumbnail.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/PaperContainer.tsx?styleId=PaperThumbnail";
import _ContentContainer from "PaperContainer-ContentContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/PaperContainer.tsx?styleId=ContentContainer";
import _ButtonContainer from "PaperContainer-ButtonContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/PaperContainer.tsx?styleId=ButtonContainer";
import _PaperCard from "PaperContainer-PaperCard.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/PaperContainer.tsx?styleId=PaperCard";
import _PaperInfo from "PaperContainer-PaperInfo.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/PaperContainer.tsx?styleId=PaperInfo";


export type PaperSize = 'xsmall' | 'small' | 'medium' | 'large';

const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

const Metadata = /*#__PURE__*/styled("div", null, {
  displayName: "Metadata",
  styles: _Metadata
});

const PaperTitle = /*#__PURE__*/styled(Body, null, {
  displayName: "PaperTitle",
  styles: _PaperTitle
});

const MetadataText = /*#__PURE__*/styled(Body, null, {
  displayName: "MetadataText",
  styles: _MetadataText
});

const PaperThumbnail = /*#__PURE__*/styled("div", null, {
  displayName: "PaperThumbnail",
  styles: _PaperThumbnail
});

const ContentContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ContentContainer",
  styles: _ContentContainer
});

const ButtonContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ButtonContainer",
  styles: _ButtonContainer
});

const PaperCard = /*#__PURE__*/styled("div", null, {
  displayName: "PaperCard",
  styles: _PaperCard
});

const PaperInfo = /*#__PURE__*/styled("div", null, {
  displayName: "PaperInfo",
  styles: _PaperInfo
});

type Props = {
  work: PaperUpdateUnlinked;
  showBookmark?: boolean;
  showDownload?: boolean;
  showInlineReader?: boolean;
  isBookmarked?: boolean;
  downloadUrl?: string;
  size?: PaperSize;
  style?: React.CSSProperties;
  className?: string;
  onDownload?: () => void;
  onBookmark?: () => void;
  onUnbookmark?: () => void;
  impressionProps?: ImpressionProps;
  onsitePodcastPromotion?: boolean;
};

// Type guard to check if work has an id
const hasId = (work: Paper | PaperUpdateUnlinked): work is Paper => {
  return 'id' in work && typeof (work as { id?: unknown }).id === 'number';
};

const PaperContainer: React.FunctionComponent<Props> = ({
  work,
  showBookmark = false,
  showDownload = false,
  showInlineReader = false,
  isBookmarked,
  downloadUrl,
  size = 'large',
  style,
  className,
  onDownload = () => null,
  onBookmark = () => null,
  onUnbookmark = () => null,
  impressionProps,
  onsitePodcastPromotion = false,
}) => {
  const { url, title, authors, publicationYear, publication, attachment } =
    work;
  const [loading, setLoading] = useState(false);
  const [showReader, setShowReader] = useState(false);

  const showAllContent = size === 'medium' || size === 'large';
  const publicationInfo: string[] = [];
  if (publicationYear) publicationInfo.push(publicationYear.toString());
  if (publication) publicationInfo.push(publication);

  useScrollLock(showReader);

  const handleDownload = async () => {
    window.open(downloadUrl, '_blank')?.focus();
    await onDownload();
  };

  const handleBookmark = async (callback: () => void) => {
    try {
      setLoading(true);
      await callback();
    } finally {
      setLoading(false);
    }
  };

  let { source } = impressionProps || {};
  source = source || 'paperContainer';

  const content = (
    <PaperCard size={size}>
      <PaperInfo>
        <PaperThumbnail size={size}>
          {attachment?.thumbnailUrl != null ? (
            <img src={attachment.thumbnailUrl} alt="" />
          ) : (
            <NoThumbnailIcon />
          )}
        </PaperThumbnail>

        <ContentContainer size={size}>
          <Metadata>
            <PaperTitle size="sm" bold paperSize={size}>
              {title}
            </PaperTitle>

            {showAllContent && (
              <div>
                {authors?.length > 0 && (
                  <MetadataText size="xs">
                    by {authors.map((p) => p.displayName).join(', ')}
                  </MetadataText>
                )}
                {publicationInfo.length > 0 && (
                  <MetadataText size="xs">
                    {publicationInfo.join(' • ')}
                  </MetadataText>
                )}
              </div>
            )}
          </Metadata>

          {showAllContent && (
            <ButtonContainer>
              {showDownload && downloadUrl && (
                <ImpressionWatcher
                  {...impressionProps}
                  source={`${source}_download-button`}
                >
                  <Button
                    icon={<DownloadIcon size={18} />}
                    variant="inverse-gray"
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                </ImpressionWatcher>
              )}
              {showBookmark && (
                <ImpressionWatcher
                  {...impressionProps}
                  source={`${source}_bookmark-button`}
                >
                  {isBookmarked ? (
                    <Button
                      icon={<CheckIcon size={18} />}
                      variant="inverse-gray"
                      disabled={loading}
                      onClick={() => handleBookmark(onUnbookmark)}
                    >
                      Bookmarked
                    </Button>
                  ) : (
                    <Button
                      icon={<PlusIcon size={18} />}
                      variant="inverse-gray"
                      disabled={loading}
                      onClick={() => handleBookmark(onBookmark)}
                    >
                      Bookmark
                    </Button>
                  )}
                </ImpressionWatcher>
              )}
            </ButtonContainer>
          )}
        </ContentContainer>
      </PaperInfo>

      {onsitePodcastPromotion && hasId(work) && (
        <SimplePaperPodcast
          workId={work.id}
          workTitle={work.title}
          showTitle={false}
          impressionSource="paper_to_podcast_newsfeed"
          onsitePodcastPromotion={true}
        />
      )}
    </PaperCard>
  );

  return (
    <Container style={style} className={className}>
      {showInlineReader && attachment?.readable && showReader && (
        <Reader
          open
          work={work}
          impressionProps={impressionProps}
          onDownload={handleDownload}
          onClose={() => setShowReader(false)}
        />
      )}

      {url ? (
        <a
          href={url}
          style={{ textDecoration: 'none' }}
          target={'_blank'}
          // Always track impressions, but stop opening the link if button got pressed
          onClick={(event) => {
            if (
              event.target instanceof Element &&
              event.target.closest('button') !== null
            ) {
              // Stop link opening if paper button got clicked
              event.preventDefault();
              event.stopPropagation();
            } else if (showInlineReader && attachment?.readable) {
              event.preventDefault();
              setShowReader(true);
            }
          }}
        >
          {content}
        </a>
      ) : (
        content
      )}
    </Container>
  );
};

export default PaperContainer;
